import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { logger } from '@app/core/helpers/logger';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { ContentCardModule } from '@app/library/content-card/content-card.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { GalleryContentGroupComponent } from '@app/library/panes/gallery-drag-scroll/gallery-content-group/gallery-content-group.component';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { ScreenService } from '@core/services';
import { IconComponent } from '@shared/icon/icon.component';
import { Content } from 'lingo2-models';
import { takeUntil } from 'rxjs/operators';
import { AbstractContentPane } from '../models/abstract-content-pane';
import { ESizeCard } from './gallery-content-card/gallery-content-card.component';

@Component({
  selector: 'app-gallery-drag-scroll',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    SpinnersModule,
    LibraryElementsModule,

    ScrollingModule,
    ModalModule,
    MobileModalModule,
    ContentCardModule,
    GalleryContentGroupComponent,
    IconComponent,
  ],
  templateUrl: './gallery-drag-scroll.component.html',
  styleUrls: ['./gallery-drag-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryDragScrollComponent extends AbstractContentPane implements OnInit, OnDestroy {
  private screenService = inject(ScreenService);
  private location = inject(Location);

  @ViewChild('scroller', { read: CdkVirtualScrollViewport })
  scroller: CdkVirtualScrollViewport;

  public groupsContent: Content[][];
  public isDragScrollEnabled: boolean;
  public isVisible: boolean;
  public itemSize = 84;
  public chunkLength: number;
  public sizeCard: ESizeCard = ESizeCard.normal;
  public openedCardIndex: number = null;
  public showModal = false;
  public isSmallScreen = true;
  public reachedPrev = false;
  public reachedNext = false;
  public item: Content = null;

  private originalUrl: string;

  public ngOnInit(): void {
    this.screenService.width$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.isSmallScreen = this.screenService.isSmallScreen;
      },
      error: (error) => {
        this.isSmallScreen = false;
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  protected onContentLoaded(viaQuery?: boolean): void {
    // throw new Error('Method not implemented.');
    this.groupsContentInit();
    this.groupsContent = this.chunk(this.content, this.chunkLength); // по 4 элементов в группе (1 столбец по 4 элемента)
    this.isDragScrollEnabled = this.deviceService.isDesktop();
    this.isVisible = !!this.content.length;
  }

  public chunk(arr, size) {
    return arr.reduce((acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), []);
  }

  public groupsContentInit() {
    if (this.content.length >= 10 && this.content.length < 24) {
      this.chunkLength = 2;
      this.sizeCard = ESizeCard.normal;
      this.itemSize = 180;
    } else if (this.content.length >= 24 && this.content.length < 40) {
      this.chunkLength = 3;
      this.sizeCard = ESizeCard.mid;
      this.itemSize = 120;
    } else if (this.content.length >= 40) {
      this.chunkLength = 4;
      this.sizeCard = ESizeCard.micro;
      this.itemSize = 100;
    } else {
      logger.warn('GalleryDragScrollComponent - no display', this.content.length, this.chunkLength, this.sizeCard);
    }
  }

  moveLeft() {
    this.scroller.scrollToIndex(0, 'smooth');
  }

  moveRight() {
    const offset = this.scroller.measureScrollOffset('left');
    this.scroller.scrollToOffset(offset + this.itemSize, 'smooth');
  }

  public trackByFn(index, item: Content[][]) {
    return item;
  }

  public onOpenCard(item: Content) {
    // logger.log('GalleryDragScrollComponent:onOpenCard', item);
    this.openedCardIndex = this.content.findIndex((el) => el.id === item.id);
    this.item = item;
    const url = this.contentRoute(this.item);
    const currentUrl = this.location.path(false);
    if (currentUrl !== url) {
      if (!this.originalUrl) {
        this.originalUrl = currentUrl;
      }
      this.location.replaceState(url);

      // отправка событий в аналитику для имитации навигации по страницам
      this.analytics.pageView(url, currentUrl);
    }
    this.checkReached();
    this.showModal = true;
  }

  public onCloseCard() {
    // logger.log('GalleryDragScrollComponent:onCloseCard');
    this.openedCardIndex = null;
    this.item = null;
    if (this.originalUrl) {
      this.location.replaceState(this.originalUrl);
      this.originalUrl = null;
    }
    this.showModal = false;
  }

  public onCloseModal(event) {
    if (event) {
      this.onCloseCard();
    }
  }

  public checkReached() {
    const reachedPrev = this.openedCardIndex <= 0;
    const reachedNext = this.openedCardIndex >= this.content.length - 1;

    if (!reachedPrev && !reachedNext) {
      this.reachedPrev = this.reachedNext = false;
    } else if (reachedPrev && reachedNext) {
      this.reachedPrev = this.reachedNext = true;
    } else if (reachedPrev) {
      this.reachedPrev = true;
    } else if (reachedNext) {
      this.reachedNext = true;
    }
  }

  public togglePrev(event?) {
    if (this.openedCardIndex - 1 >= 0) {
      this.onOpenCard(this.content[this.openedCardIndex - 1]);
    }
    this.checkReached();
  }

  public toggleNext(event?) {
    if (this.openedCardIndex + 1 <= this.content.length - 1) {
      this.onOpenCard(this.content[this.openedCardIndex + 1]);
    }
    this.checkReached();
  }
}
